import axios from "axios";
import { setAuthorization } from "./api_helper";
import * as url from "./url_helper";

/**
 * Impersonación para soporte / verificación interna.
 *
 * Modelo: el actor sigue autenticado con SU cuenta (su cookie de refresh es la
 * fuente de verdad y no se toca). Al impersonar guardamos aparte el access token
 * del usuario objetivo y un registro `kronaly_impersonation`; toda la SPA se
 * re-hidrata como ese usuario tras un reload. "Salir" es instantáneo: basta con
 * pedir /auth/refresh (ya sin el flag) para recuperar el access token del actor.
 *
 * La renovación del token del objetivo cuando expira (cada 15 min) la hace el
 * interceptor 401 de api_helper, que reconvierte el token del actor en el del
 * objetivo mientras el flag esté presente.
 */

const KEY = "kronaly_impersonation";

export function getImpersonation() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function isImpersonating() {
  return !!localStorage.getItem(KEY);
}

function persistAccess(access) {
  const raw = localStorage.getItem("authUser");
  const u = raw ? JSON.parse(raw) : {};
  u.access_token = access;
  localStorage.setItem("authUser", JSON.stringify(u));
  setAuthorization(access);
}

function postImpersonate(email) {
  return axios.post(
    `${axios.defaults.baseURL}${url.POST_KRONALY_IMPERSONATE}`,
    { email },
    { withCredentials: true },
  );
}

/**
 * Entra "como" el usuario `email`. Recarga la app al terminar.
 */
export async function startImpersonation(email) {
  let r;
  try {
    r = await postImpersonate(email);
  } catch (e) {
    // Token de actor expirado: refrescamos con su cookie y reintentamos una vez.
    if (e?.status === 401) {
      const rr = await axios.post(
        `${axios.defaults.baseURL}${url.POST_KRONALY_REFRESH}`,
        {},
        { withCredentials: true },
      );
      const inner = rr?.data ?? rr;
      if (inner?.access_token) setAuthorization(inner.access_token);
      r = await postImpersonate(email);
    } else {
      throw e;
    }
  }

  const body = r?.data ?? r;
  const access = body?.access_token;
  if (!access) throw new Error("El servidor no devolvió un token de impersonación.");

  const record = {
    email: body?.user?.email || email,
    name: body?.user?.name || email,
    actorEmail: body?.actor?.email || null,
    actorName: body?.actor?.name || null,
    // Workspace del actor: lo restauramos al salir.
    prevWorkspaceId: localStorage.getItem("kronaly_workspace_id") || null,
  };
  localStorage.setItem(KEY, JSON.stringify(record));

  persistAccess(access);

  // El objetivo puede no ser miembro del workspace activo del actor. Soltamos el
  // id del actor y fijamos el primer workspace del objetivo (mismo criterio que
  // el login), para no dejar la sesión sin X-Workspace-Id — lo que rompe todos
  // los endpoints workspace-scoped ("El header X-Workspace-Id es requerido").
  localStorage.removeItem("kronaly_workspace_id");
  try {
    const wsResponse = await axios.get(
      `${axios.defaults.baseURL}${url.GET_WORKSPACES}`,
      { withCredentials: true, headers: { Authorization: "Bearer " + access } },
    );
    const list = wsResponse?.data ?? wsResponse;
    const arr = Array.isArray(list) ? list : [];
    if (arr.length > 0 && arr[0].id) {
      localStorage.setItem("kronaly_workspace_id", arr[0].id);
    }
  } catch (_) {
    /* sin workspaces: el WorkspaceSwitcher mostrará el estado vacío */
  }

  window.location.assign("/");
}

/**
 * Sale de la impersonación y vuelve a la sesión del actor.
 */
export async function stopImpersonation() {
  const rec = getImpersonation();
  localStorage.removeItem(KEY);

  if (rec?.prevWorkspaceId) {
    localStorage.setItem("kronaly_workspace_id", rec.prevWorkspaceId);
  } else {
    localStorage.removeItem("kronaly_workspace_id");
  }

  try {
    const r = await axios.post(
      `${axios.defaults.baseURL}${url.POST_KRONALY_REFRESH}`,
      {},
      { withCredentials: true },
    );
    const inner = r?.data ?? r;
    if (inner?.access_token) persistAccess(inner.access_token);
  } catch (_) {
    // Si el refresh del actor falla, el arranque/interceptor llevará a /login.
  }

  window.location.assign("/");
}
